import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CalendarPicker from '../webform/CalendarPicker';
import { XMarkIcon, MagnifyingGlassIcon, PlusIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { BarcodeScanner } from '../common/BarcodeScanner';
import { useFacturaForm } from '../../shared/nuevaFactura/useFacturaForm';
import type { ClienteForm, ProductoLinea } from '../../shared/nuevaFactura/facturaFormTypes';
import { useAuthStore } from '../../stores/useAuthStore';
import { useClientesStore } from '../../stores/useClientesStore';
import { useProductosStore } from '../../stores/useProductosStore';
import { useFacturasStore } from '../../stores/useFacturasStore';
import { usePaymentMethodsStore } from '../../stores/usePaymentMethodsStore';
import {
  buildFacturaNuevaDesdeFormulario,
  isCreditoPaymentMethod,
  metodoPagoDbDesdeMetodoPago,
} from '../../lib/nuevaFactura/buildFacturaNuevaDesdeFormulario';
import { throwIfFacturaMutationFailed } from '../../utils/facturaMutations';
import type { Producto } from '../../types';
import { isProductoArchivado } from '../../utils/productoEstado';
import { clsx } from 'clsx';
import type { Factura } from './types';
import {
  buildFacturaPdfBlob,
  downloadFacturaPdfBlob,
  safeFacturaPdfFilename,
  shareFacturaPdfBlob,
  canSharePdfFiles,
} from '../../utils/facturaPdfBlob';
import { numeroFacturaParaMostrar } from '../../utils/facturaNumeroCanal';
import {
  getPaymentMethodDisplayFields,
  getPaymentMethodQrUrl,
} from '../../utils/paymentMethodMedia';
import { PaymentMethodFieldValue } from '../shared/PaymentMethodMedia';
import { supabase } from '../../lib/supabase';
import type { PaymentMethod } from '../../stores/usePaymentMethodsStore';
import { ClienteCreditosTab } from '../cobros/ClienteCreditosTab';
import {
  buildFormStateFromFactura,
  fechaToDatetimeLocalInput,
  matchPaymentMethodIdFromFacturaDb,
} from '../../lib/nuevaFactura/hydrateFacturaFormFromFactura';
import { METODO_PAGO_SISTEMA_CREDITO_ID, metodoPagoSistemaCredito } from '../../lib/nuevaFactura/sistemaMetodoCredito';
import type { FacturaTotales } from '../../shared/nuevaFactura/facturaFormTypes';
import { Link } from 'react-router-dom';
import {
  toYmdLocal,
  ymdAddDays,
  ymdFinDeMesLocal,
  partesIgualesCOP,
  fechasPlanCuotas,
} from '../../lib/credito/cuotasPlan';
import type { CreditoCrearConfig } from './types';
import { loadFeConfigLocal, getFeEstado } from '../../lib/facturacionElectronicaApi';
import {
  feMissingClientFields,
  feMissingLabels,
} from '../../lib/feClientRequirements';

/** Crédito simple guardado: mostrar caja con enlace a Cobros y Créditos (el insert ya corrió en el store). */
function facturaEsCreditoConSaldo(f: Factura | null | undefined): boolean {
  if (!f) return false;
  if (String(f.metodoPago ?? '').toUpperCase() !== 'CREDITO') return false;
  const total = Number(f.total) || 0;
  const pagado = Number(f.montoPagado ?? 0) || 0;
  return total > 0 && pagado < total;
}

function mergeFacturaForUpdate(base: Factura, borrador: Factura, totales: FacturaTotales): Factura {
  return {
    ...base,
    cliente: borrador.cliente,
    items: borrador.items,
    subtotalSinIva: borrador.subtotalSinIva,
    totalIva: borrador.totalIva,
    subtotal: borrador.subtotal,
    descuento: borrador.descuento,
    total: borrador.total,
    nuevaFlujoTotales: totales,
    estadoPago: borrador.estadoPago,
    montoPagado: borrador.montoPagado,
    montoPendiente: borrador.montoPendiente,
    metodoPago: borrador.metodoPago,
    fechaPago: borrador.fechaPago,
    fechaEntrega: borrador.fechaEntrega,
    tipoEntrega: borrador.tipoEntrega,
    metodoEntrega: borrador.metodoEntrega,
    costoEnvio: borrador.costoEnvio,
    notasEntrega: borrador.notasEntrega,
    direccionEntrega: borrador.direccionEntrega,
    notas: borrador.notas,
    tipoDescuento: borrador.tipoDescuento,
    valorDescuento: borrador.valorDescuento,
    esRegalo: borrador.esRegalo,
    mensajeRegalo: borrador.mensajeRegalo,
    direccionRegalo: borrador.direccionRegalo,
    fechaEntregaAgendada: borrador.fechaEntregaAgendada,
    agendarCalendario: borrador.agendarCalendario,
    creditoCrearConfig: borrador.creditoCrearConfig,
    id: base.id,
    numero: base.numero,
    fechaCreacion: base.fechaCreacion,
    canalVenta: base.canalVenta,
    estado: base.estado,
    estadoEntrega: base.estadoEntrega,
    estadoVerificacion: base.estadoVerificacion,
    citaId: base.citaId,
    historialPagos: base.historialPagos,
    comprobanteUrl: base.comprobanteUrl,
    fotoEntrega: base.fotoEntrega,
    fechaEntregaReal: base.fechaEntregaReal,
    ultimaActividad: new Date(),
  };
}

const C = {
  blue: '#185FA5',
  blueBg: '#E6F1FB',
  green: '#1D9E75',
  border: 'rgba(0,0,0,0.12)',
  surface: '#F8F8F7',
  textSecondary: '#6B6B6B',
  textHint: '#9E9E9E',
};

/** `pedidos`: copy y secciones orientadas a la cola de pedidos (crédito visible primero). */
export type NuevaFacturaWizardContext = 'default' | 'pedidos';

const STEP_TITLES_DEFAULT = ['Cliente y productos', 'Entrega y pago', 'Confirmar'];
const STEP_TITLES_PEDIDOS = [
  'Cliente y productos',
  'Entrega, pago y crédito',
  'Confirmar pedido',
];

/** Valores para CalendarPicker / factura.fechaEntregaAgendada (hora local). */
function splitAgendaLocal(iso: string): { date: string | null; time: string | null } {
  const t = iso.trim();
  if (!t) return { date: null, time: null };
  const m = t.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (m?.[1] && m[2]) return { date: m[1], time: m[2] };
  return { date: null, time: null };
}

function iconForPaymentMethodType(t: string): string {
  switch (t) {
    case 'efectivo':
      return '💵';
    case 'transferencia':
      return '🏦';
    case 'tarjeta':
    case 'tarjeta_debito':
      return '💳';
    case 'nequi':
      return '📲';
    case 'daviplata':
      return '📱';
    case 'pse':
      return '🌐';
    case 'link_pago':
      return '🔗';
    case 'qr_code':
      return '📷';
    case 'credito':
      return '🧾';
    default:
      return '💰';
  }
}

function fmtCop(n: number) {
  return '$' + Math.round(n).toLocaleString('es-CO');
}

function tipoOfertaLabel(p: Producto): string {
  const t = String(p.tipoOferta || '').toLowerCase();
  if (t === 'servicio') return 'SERVICIO';
  if (t === 'membresia') return 'MEMBRESÍA';
  if (t === 'listado') return 'LISTADO';
  return 'PRODUCTO';
}

function stockEtiqueta(p: Producto): string {
  if (isProductoArchivado(p)) return 'Stock: Borrado';
  if (p.inventory_type === 'unlimited') return 'Stock: sin límite';
  return `Stock: ${p.stock ?? 0}`;
}

function precioSinIvaMostrar(p: Producto): number | null {
  const ivaPct = Number(p.ivaPorcentaje) || 0;
  const precioCon = Number(p.precioVenta) || 0;
  if (ivaPct <= 0) return null;
  if (p.precioSinIva != null && !Number.isNaN(Number(p.precioSinIva))) return Number(p.precioSinIva);
  return precioCon / (1 + ivaPct / 100);
}

export interface NuevaFacturaFlujoModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (facturaId: string) => void;
  /** Si viene informada, el flujo actualiza la fila existente (sin crear factura ni tocar stock de nuevo). */
  facturaToEdit?: Factura | null;
  onUpdated?: () => void;
  /** Preselección al abrir (Mensajes, CRM, etc.). */
  initialCliente?: ClienteForm | null;
  /** Líneas iniciales (p. ej. convertir pedido/lead). */
  initialProductoLineas?: ProductoLinea[] | null;
  /** `pedidos`: resalta crédito y opciones al cliente en el paso de pago (mismo wizard que facturas). */
  wizardContext?: NuevaFacturaWizardContext;
}

export function NuevaFacturaFlujoModal({
  open,
  onClose,
  onCreated,
  facturaToEdit = null,
  onUpdated,
  initialCliente = null,
  initialProductoLineas = null,
  wizardContext = 'pedidos',
}: NuevaFacturaFlujoModalProps) {
  const { currentEmpresa } = useAuthStore();
  const { clientes, fetchClientes, createCliente, updateCliente } = useClientesStore();
  const { productos, fetchProductos, findByBarcode } = useProductosStore();
  const { createFactura, updateFactura } = useFacturasStore();
  const paymentMethods = usePaymentMethodsStore((s) => s.paymentMethods);
  const fetchPaymentMethods = usePaymentMethodsStore((s) => s.fetchPaymentMethods);
  const getActivePaymentMethods = usePaymentMethodsStore((s) => s.getActivePaymentMethods);
  const formApi = useFacturaForm();
  const {
    form,
    step,
    totales,
    canProceed,
    TOTAL_STEPS,
    goNext,
    goBack,
    reset,
    replaceForm,
    setCliente,
    setNumeroFactura,
    addProducto,
    setProductos,
    removeProducto,
    updateQty,
    setDescuento,
    setDescuentoValor,
    setTipoEntrega,
    setCostoEnvio,
    setMetodoEntrega,
    setNotasEntrega,
    setCobrarEnvioAlDestino,
    setFechaEntrega,
    setDireccionEntrega,
    setMetodoPago,
    setAgendarCalendario,
    toggleRegalo,
    setNotas,
    setCreditoAjusteTipo,
    setCreditoAjusteValor,
    setCreditoAjusteSentido,
  } = formApi;

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [facturaCreadaParaPdf, setFacturaCreadaParaPdf] = useState<Factura | null>(null);
  const [pdfShareBusy, setPdfShareBusy] = useState(false);
  const sharePdfAvailable = useMemo(() => canSharePdfFiles(), []);
  const [clienteSearch, setClienteSearch] = useState('');
  const [feEmpresaActiva, setFeEmpresaActiva] = useState(false);
  const [emitirFacturaElectronica, setEmitirFacturaElectronica] = useState(false);
  const [showClienteHits, setShowClienteHits] = useState(false);
  const [showProductos, setShowProductos] = useState(false);
  const [montoPagado, setMontoPagado] = useState(0);
  const [creditoModo, setCreditoModo] = useState<'simple' | 'plan_pagos'>('simple');
  const [fechaVencSimple, setFechaVencSimple] = useState(() =>
    ymdAddDays(toYmdLocal(new Date()), 30)
  );
  const [numCuotas, setNumCuotas] = useState(3);
  const [frecuenciaDias, setFrecuenciaDias] = useState<7 | 15 | 30>(30);
  const [primeraFechaPlan, setPrimeraFechaPlan] = useState(() =>
    ymdAddDays(toYmdLocal(new Date()), 7)
  );
  const [mensajeRegalo, setMensajeRegalo] = useState('');
  const [direccionRegalo, setDireccionRegalo] = useState('');
  const [fechaAgendada, setFechaAgendada] = useState('');
  const [comprobanteFile, setComprobanteFile] = useState<File | null>(null);
  const [comprobantePreview, setComprobantePreview] = useState<string | null>(null);
  const [uploadingComprobante, setUploadingComprobante] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevOpenRef = useRef(false);
  const editHydratedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen || !currentEmpresa?.id) return;
    if (facturaToEdit) return;
    if (initialCliente) {
      setCliente(initialCliente);
      setClienteSearch(initialCliente.nombre);
      setShowClienteHits(false);
    }
    if (initialProductoLineas && initialProductoLineas.length > 0) {
      setProductos(initialProductoLineas);
    }
  }, [open, currentEmpresa?.id, facturaToEdit, initialCliente, initialProductoLineas, setCliente, setProductos]);

  useEffect(() => {
    if (!open || !currentEmpresa?.id) return;
    void fetchClientes(currentEmpresa.id);
    void fetchProductos(currentEmpresa.id);
    void fetchPaymentMethods(currentEmpresa.id);
    let cancelled = false;
    const empresaId = currentEmpresa.id;

    (async () => {
      try {
        // Prefer Netlify estado (same as Configuración); local RLS read as fallback.
        let cfg: Awaited<ReturnType<typeof loadFeConfigLocal>> = null;
        try {
          const res = await getFeEstado(empresaId, false);
          cfg = res.config;
        } catch {
          cfg = await loadFeConfigLocal(empresaId);
        }
        if (cancelled) return;
        // Match Configuración «Activa» badge (enabled + estado). UUID checked at emit time.
        const on = Boolean(cfg?.enabled && cfg.estado === 'activo');
        setFeEmpresaActiva(on);
        // Per-pedido opt-in: show checkbox, default off so FE is never forced.
        setEmitirFacturaElectronica(false);
      } catch {
        if (!cancelled) {
          setFeEmpresaActiva(false);
          setEmitirFacturaElectronica(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, currentEmpresa?.id, fetchClientes, fetchProductos, fetchPaymentMethods]);

  const stepTitles = useMemo(
    () => (wizardContext === 'pedidos' ? STEP_TITLES_PEDIDOS : STEP_TITLES_DEFAULT),
    [wizardContext]
  );

  const invoiceMethods = useMemo(() => {
    const eid = currentEmpresa?.id;
    if (!eid) return [];
    const fromConfig = (paymentMethods || [])
      .filter(
        (pm) =>
          pm.empresa_id === eid &&
          pm.is_active &&
          pm.show_on_invoices !== false &&
          !isCreditoPaymentMethod(pm)
      )
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    return [...fromConfig, metodoPagoSistemaCredito(eid)];
  }, [paymentMethods, currentEmpresa?.id]);

  useEffect(() => {
    if (!open) {
      editHydratedIdRef.current = null;
      return;
    }
    if (!facturaToEdit?.id || !currentEmpresa?.id) return;
    if (invoiceMethods.length === 0) return;
    if (editHydratedIdRef.current === facturaToEdit.id) return;

    const pmId =
      matchPaymentMethodIdFromFacturaDb(facturaToEdit.metodoPago, invoiceMethods) ??
      invoiceMethods[0]!.id;
    const nextForm = buildFormStateFromFactura(facturaToEdit, pmId);
    replaceForm(nextForm);
    setClienteSearch(nextForm.cliente?.nombre || '');
    setShowClienteHits(false);
    setMontoPagado(Math.max(0, Number(facturaToEdit.montoPagado) || 0));
    setMensajeRegalo(facturaToEdit.mensajeRegalo || '');
    setDireccionRegalo(facturaToEdit.direccionRegalo || '');
    setFechaAgendada(
      facturaToEdit.fechaEntregaAgendada
        ? fechaToDatetimeLocalInput(new Date(facturaToEdit.fechaEntregaAgendada))
        : '',
    );
    setDone(false);
    setFacturaCreadaParaPdf(null);
    editHydratedIdRef.current = facturaToEdit.id;
  }, [open, facturaToEdit, currentEmpresa?.id, invoiceMethods, replaceForm]);

  const metodoPagoResumen = useMemo(() => {
    if (form.metodo_pago === METODO_PAGO_SISTEMA_CREDITO_ID) return 'Crédito';
    const pm = invoiceMethods.find((m) => m.id === form.metodo_pago);
    if (pm) return pm.method_name?.trim() || pm.method_type;
    return '—';
  }, [invoiceMethods, form.metodo_pago]);

  const metodoPagoElegido = useMemo(
    () => invoiceMethods.find((m) => m.id === form.metodo_pago),
    [invoiceMethods, form.metodo_pago]
  );
  const confirmarEsCredito = Boolean(
    form.metodo_pago === METODO_PAGO_SISTEMA_CREDITO_ID ||
      (metodoPagoElegido && isCreditoPaymentMethod(metodoPagoElegido))
  );

  const credConfigOk = useMemo(() => {
    if (step !== 1) return true;
    const pm = invoiceMethods.find((m) => m.id === form.metodo_pago);
    if (!pm || !isCreditoPaymentMethod(pm)) return true;
    const paidC = Math.max(0, Math.min(montoPagado, totales.total));
    const saldo = Math.max(0, totales.total - paidC);
    if (saldo <= 0) return true;
    if (creditoModo === 'simple') {
      return (
        /^\d{4}-\d{2}-\d{2}$/.test(fechaVencSimple) &&
        !Number.isNaN(new Date(`${fechaVencSimple}T12:00:00`).getTime())
      );
    }
    return (
      numCuotas >= 2 &&
      numCuotas <= 36 &&
      /^\d{4}-\d{2}-\d{2}$/.test(primeraFechaPlan) &&
      !Number.isNaN(new Date(`${primeraFechaPlan}T12:00:00`).getTime())
    );
  }, [
    step,
    invoiceMethods,
    form.metodo_pago,
    montoPagado,
    totales.total,
    creditoModo,
    fechaVencSimple,
    numCuotas,
    primeraFechaPlan,
  ]);

  const canAdvance = useMemo(() => {
    if (!canProceed) return false;
    if (step === 1 && invoiceMethods.length === 0) return false;
    if (step === 1 && !form.metodo_pago) return false;
    if (step === 1 && !credConfigOk) return false;
    if (step === 1 && !form.cliente?.id) return false;
    if (step === 1 && form.agendar_calendario && !fechaAgendada.trim()) return false;
    return true;
  }, [
    canProceed,
    step,
    form.metodo_pago,
    form.cliente?.id,
    form.agendar_calendario,
    invoiceMethods.length,
    fechaAgendada,
    credConfigOk,
  ]);

  useEffect(() => {
    if (!open || step !== 1) return;
    const n = Math.max(2, Math.min(36, Math.floor(numCuotas)));
    const montoCuota =
      creditoModo === 'plan_pagos' && n >= 2 ? Math.round(totales.total / n) : null;
    console.log('=== CREDITO CONFIG ===', {
      numCuotas,
      frecuenciaDias,
      primeraFechaVencimiento: primeraFechaPlan,
      montoCuota,
    });
  }, [
    open,
    step,
    numCuotas,
    frecuenciaDias,
    primeraFechaPlan,
    totales.total,
    creditoModo,
  ]);

  useEffect(() => {
    if (!open) return;
    const allowedIds = new Set(invoiceMethods.map((m) => m.id));
    const valid = form.metodo_pago && allowedIds.has(form.metodo_pago);
    if (!valid) {
      const first = invoiceMethods[0];
      if (first) setMetodoPago(first.id);
    }
  }, [open, invoiceMethods, form.metodo_pago, setMetodoPago]);

  useEffect(() => {
    if (!open) {
      reset();
      setDone(false);
      setFacturaCreadaParaPdf(null);
      setClienteSearch('');
      setShowClienteHits(false);
      setShowProductos(false);
      setMontoPagado(0);
      setCreditoModo('simple');
      setFechaVencSimple(ymdAddDays(toYmdLocal(new Date()), 30));
      setNumCuotas(3);
      setFrecuenciaDias(30);
      setPrimeraFechaPlan(ymdAddDays(toYmdLocal(new Date()), 7));
      setMensajeRegalo('');
      setDireccionRegalo('');
      setFechaAgendada('');
      setComprobanteFile(null);
      setComprobantePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setUploadingComprobante(false);
    }
  }, [open, reset]);

  const clientesForm: ClienteForm[] = useMemo(() => {
    const eid = currentEmpresa?.id;
    return (clientes || [])
      .filter((c) => !eid || c.empresaId === eid)
      .map((c) => ({
        id: c.id,
        nombre: [c.nombre, c.apellido].filter(Boolean).join(' ').trim() || 'Sin nombre',
        telefono: c.telefono,
        cedula: c.numeroDocumento,
        email: c.email,
        tipoDocumento: c.tipoDocumento || 'CC',
      }));
  }, [clientes, currentEmpresa?.id]);

  const filtrados = useMemo(() => {
    const q = clienteSearch.trim().toLowerCase();
    if (!q) return [];
    return clientesForm.filter(
      (c) =>
        c.nombre.toLowerCase().includes(q) ||
        (c.telefono && c.telefono.includes(clienteSearch.trim())) ||
        (c.cedula && c.cedula.includes(clienteSearch.trim()))
    );
  }, [clienteSearch, clientesForm]);

  const handleNext = async (finalize = false) => {
    if (step < TOTAL_STEPS - 1) {
      if (step === 0 && emitirFacturaElectronica) {
        const missing = feMissingClientFields(form.cliente);
        if (missing.length) {
          alert(
            `Para facturación electrónica falta: ${feMissingLabels(missing).join(', ')}. Complétalo en el cliente antes de continuar.`
          );
          return;
        }
      }
      goNext();
      return;
    }
    if (!currentEmpresa?.id) return;
    setSubmitting(true);
    try {
      const isSistemaCredito = form.metodo_pago === METODO_PAGO_SISTEMA_CREDITO_ID;
      const pmSelected = isSistemaCredito
        ? metodoPagoSistemaCredito(currentEmpresa.id)
        : invoiceMethods.find((m) => m.id === form.metodo_pago);
      if (!pmSelected) {
        alert('Selecciona una forma de pago.');
        return;
      }
      const metodoPagoDb = metodoPagoDbDesdeMetodoPago(pmSelected);
      const paidCapped = Math.max(0, Math.min(Number(montoPagado) || 0, totales.total));
      const credito = isCreditoPaymentMethod(pmSelected);
      /** Comprobante: pago inmediato, o crédito con abono > 0. */
      const permitirComprobante = !credito || paidCapped > 0;
      const saldoCred = Math.max(0, totales.total - paidCapped);
      if (credito && saldoCred > 0) {
        if (creditoModo === 'simple') {
          const fv = String(fechaVencSimple || '').trim();
          if (!/^\d{4}-\d{2}-\d{2}$/.test(fv) || Number.isNaN(new Date(`${fv}T12:00:00`).getTime())) {
            alert('La fecha de vencimiento es obligatoria para créditos en un solo pago.');
            return;
          }
        } else {
          const pf = String(primeraFechaPlan || '').trim();
          const n = Math.min(36, Math.max(2, Math.floor(numCuotas)));
          if (n < 2 || !/^\d{4}-\d{2}-\d{2}$/.test(pf) || Number.isNaN(new Date(`${pf}T12:00:00`).getTime())) {
            alert('Indica el número de cuotas y la fecha de la primera cuota del plan de pagos.');
            return;
          }
        }
      }
      let creditoCrearConfig: CreditoCrearConfig | undefined;
      if (credito && saldoCred > 0) {
        creditoCrearConfig =
          creditoModo === 'simple'
            ? { modo: 'simple', fechaVencimientoSimple: fechaVencSimple }
            : {
                modo: 'plan_pagos',
                numCuotas: Math.min(36, Math.max(2, Math.floor(numCuotas))),
                frecuenciaDias,
                primeraFechaVencimiento: primeraFechaPlan,
              };
      }
      const buildOpts = {
        metodoPagoDb,
        montoPagado: paidCapped,
        credito,
        ...(creditoCrearConfig ? { creditoCrearConfig } : {}),
        mensajeRegalo,
        direccionRegalo,
        fechaEntregaAgendada: fechaAgendada.trim() ? new Date(fechaAgendada) : null,
      };
      const borrador = buildFacturaNuevaDesdeFormulario(form, totales, buildOpts);
      console.log('=== PAYLOAD TO createFactura ===', {
        metodo_pago: borrador.metodoPago,
        creditoCrearConfig: borrador.creditoCrearConfig,
      });
      console.log('=== IS EDIT MODE? ===', {
        isEditMode: !!facturaToEdit,
        facturaToEditId: facturaToEdit?.id,
        callingFunction: facturaToEdit?.id ? 'updateFactura' : 'createFactura',
      });

      if (facturaToEdit?.id) {
        const merged = mergeFacturaForUpdate(facturaToEdit, borrador, totales);
        const updated = await updateFactura(facturaToEdit.id, merged);
        throwIfFacturaMutationFailed(updated, 'actualizar');

        let comprobanteUrlPost: string | undefined;
        const subirComprobante =
          comprobanteFile && currentEmpresa?.id && (!credito || paidCapped > 0);
        if (subirComprobante) {
          setUploadingComprobante(true);
          try {
            const ext = comprobanteFile.name.split('.').pop() || 'jpg';
            const path = `${currentEmpresa.id}/${facturaToEdit.id}_${Date.now()}.${ext}`;
            const { error: upErr } = await supabase.storage
              .from('comprobantes')
              .upload(path, comprobanteFile, {
                contentType: comprobanteFile.type || 'application/octet-stream',
                upsert: false,
              });
            if (!upErr) {
              const { data } = supabase.storage.from('comprobantes').getPublicUrl(path);
              const url = data?.publicUrl;
              if (url) {
                await supabase
                  .from('facturas')
                  .update({ comprobante_url: url })
                  .eq('id', facturaToEdit.id)
                  .eq('empresa_id', currentEmpresa.id);
                comprobanteUrlPost = url;
              }
            }
          } catch (e) {
            console.error('comprobante upload', e);
          } finally {
            setUploadingComprobante(false);
          }
        }

        const forPdf =
          updated && comprobanteUrlPost
            ? { ...updated, comprobanteUrl: comprobanteUrlPost }
            : updated;
        setFacturaCreadaParaPdf(forPdf ?? { ...merged, id: facturaToEdit.id });
        onUpdated?.();
        setDone(true);
        return;
      }

      const created = await createFactura({
        ...borrador,
        emitirFacturaElectronica: Boolean(emitirFacturaElectronica && feEmpresaActiva),
      } as Parameters<typeof createFactura>[0] & { emitirFacturaElectronica?: boolean });
      throwIfFacturaMutationFailed(created, 'crear');
      if (!created?.id) throw new Error('No se recibió el id de la factura.');

      const feErrMsg = (created as { feEmitError?: string }).feEmitError;
      if (feErrMsg) {
        alert(
          `Pedido creado, pero la factura electrónica no se emitió:\n\n${feErrMsg}\n\n` +
            'Puedes corregir el cliente (documento/email) e intentar de nuevo, o dejar el pedido interno.'
        );
      }
      if (comprobanteFile && currentEmpresa?.id && permitirComprobante) {
        setUploadingComprobante(true);
        try {
          const ext = comprobanteFile.name.split('.').pop() || 'jpg';
          const path = `${currentEmpresa.id}/${created.id}_${Date.now()}.${ext}`;
          const { error: upErr } = await supabase.storage
            .from('comprobantes')
            .upload(path, comprobanteFile, {
              contentType: comprobanteFile.type || 'application/octet-stream',
              upsert: false,
            });
          if (!upErr) {
            const { data } = supabase.storage.from('comprobantes').getPublicUrl(path);
            const url = data?.publicUrl;
            if (url) {
              await supabase
                .from('facturas')
                .update({ comprobante_url: url })
                .eq('id', created.id)
                .eq('empresa_id', currentEmpresa.id);
            }
          }
        } catch (e) {
          console.error('comprobante upload', e);
        } finally {
          setUploadingComprobante(false);
        }
      }

      if (finalize && created?.id) {
        const finalized = await useFacturasStore.getState().completarVentaPedido(created.id);
        if (finalized === null) {
          console.error('completarVentaPedido after createFactura');
          alert(
            'La factura se creó como pedido, pero no se pudo completar como venta. ' +
              'Puedes completarla desde Pedidos → Completar venta.',
          );
        }
      }

      setFacturaCreadaParaPdf({ ...borrador, id: created.id });
      onCreated?.(created.id);

      // Cupo de agenda: `calculateAvailability` cuenta filas en `appointments` (mismo sistema que CalendarPicker).
      if (form.cliente?.id && fechaAgendada.trim() && currentEmpresa?.id) {
        try {
          const m = fechaAgendada.trim().match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
          const ymd = m?.[1];
          const hm = m?.[2];
          if (ymd && hm) {
            const [hh, mm] = hm.split(':').map((x) => Number(x));
            const timeStr = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
            const { data: st } = await supabase
              .from('scheduling_settings')
              .select('slot_duration_minutes')
              .eq('empresa_id', currentEmpresa.id)
              .maybeSingle();
            const duration = Number(st?.slot_duration_minutes) || 30;
            const { error: apErr } = await supabase.from('appointments').insert({
              empresa_id: currentEmpresa.id,
              cliente_id: form.cliente.id,
              appointment_date: ymd,
              appointment_time: timeStr,
              duration_minutes: duration,
              status: 'confirmed',
              fulfillment_type: form.tipo_entrega === 'domicilio' ? 'delivery' : 'pickup',
              notes: `Entrega agendada desde factura ${borrador.numero}`,
            });
            if (apErr) console.error('appointments (cupo entrega):', apErr);
          }
        } catch (e) {
          console.error('appointments insert', e);
        }
      }

      setDone(true);
    } catch (e) {
      alert(
        e instanceof Error
          ? e.message
          : facturaToEdit?.id
            ? 'No se pudo guardar los cambios.'
            : 'No se pudo crear la factura.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleNueva = () => {
    reset();
    setDone(false);
    setFacturaCreadaParaPdf(null);
    setClienteSearch('');
    setMontoPagado(0);
    setComprobanteFile(null);
    setComprobantePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const buildPdfCompanyInfo = () => {
    const companyName = currentEmpresa?.nombre || 'Mi Empresa';
    const companyLogo =
      (currentEmpresa as { logo_url?: string; logo?: string })?.logo_url ??
      (currentEmpresa as { logo?: string })?.logo ??
      null;
    const companyEmail = (currentEmpresa as { email?: string })?.email || '';
    const companyPhone = (currentEmpresa as { telefono?: string })?.telefono || '';
    const companyAddress = (currentEmpresa as { direccion?: string })?.direccion || '';
    const paymentMethodsForPdf = getActivePaymentMethods(currentEmpresa?.id || '').filter(
      (pm) => pm.show_on_invoices,
    );
    return {
      companyName,
      companyLogo,
      companyEmail,
      companyPhone,
      companyAddress,
      paymentMethods: paymentMethodsForPdf,
    };
  };

  const handleDescargarPdfCreada = async () => {
    if (!facturaCreadaParaPdf || !currentEmpresa?.id) return;
    setPdfShareBusy(true);
    try {
      await fetchPaymentMethods(currentEmpresa.id);
      const blob = await buildFacturaPdfBlob(facturaCreadaParaPdf, buildPdfCompanyInfo());
      const nm = numeroFacturaParaMostrar(facturaCreadaParaPdf.numero, facturaCreadaParaPdf.canalVenta);
      downloadFacturaPdfBlob(blob, safeFacturaPdfFilename(nm));
    } catch (e) {
      console.error('handleDescargarPdfCreada', e);
      alert('No se pudo generar el PDF. Intenta desde Pedidos o Ventas → Ver factura.');
    } finally {
      setPdfShareBusy(false);
    }
  };

  const handleCompartirPdfCreada = async () => {
    if (!facturaCreadaParaPdf || !currentEmpresa?.id) return;
    if (!sharePdfAvailable) {
      alert(
        'Compartir PDF no está disponible en este navegador.\n\n' +
          'Usa Safari en Mac o iPhone, o «Descargar PDF».',
      );
      return;
    }
    setPdfShareBusy(true);
    try {
      await fetchPaymentMethods(currentEmpresa.id);
      const blob = await buildFacturaPdfBlob(facturaCreadaParaPdf, buildPdfCompanyInfo());
      const nm = numeroFacturaParaMostrar(facturaCreadaParaPdf.numero, facturaCreadaParaPdf.canalVenta);
      const fname = safeFacturaPdfFilename(nm);
      const ok = await shareFacturaPdfBlob(blob, fname, {
        title: `Factura ${nm}`,
        text: `Factura ${nm} — ${(currentEmpresa as { nombre?: string })?.nombre || 'Clientelo'}`,
      });
      if (!ok) {
        alert('No se pudo abrir Compartir. Usa «Descargar PDF».');
      }
    } catch (e) {
      console.error('handleCompartirPdfCreada', e);
      alert('No se pudo generar el PDF.');
    } finally {
      setPdfShareBusy(false);
    }
  };

  const onPickProducto = useCallback(
    (p: Producto) => {
      const precio = Number(p.precioVenta) || 0;
      const ivaPct = Number(p.ivaPorcentaje ?? 0) || 0;
      addProducto({
        id: p.id,
        nombre: p.nombre,
        precio,
        iva_porcentaje: ivaPct,
      });
      setShowProductos(false);
    },
    [addProducto]
  );

  if (!open) return null;

  const isEditMode = Boolean(facturaToEdit?.id);

  if (done) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-xl font-medium text-gray-900">
            {isEditMode
              ? 'Cambios guardados'
              : wizardContext === 'pedidos'
                ? 'Pedido creado'
                : 'Factura creada'}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {form.numero_factura} · {fmtCop(totales.total)}
          </p>
          {!isEditMode && facturaEsCreditoConSaldo(facturaCreadaParaPdf) && (
            <div className="mt-4 rounded-xl border-2 border-[#1A56DB]/30 bg-blue-50 p-4 text-left">
              <p className="text-sm font-semibold text-[#0B1F4A]">Crédito registrado en el sistema</p>
              <p className="mt-1 text-sm text-gray-700">
                El saldo de esta venta quedó en <strong>Cobros y Créditos</strong> (según la fecha o plan de cuotas que definiste). Puedes
                seguirlo y registrar pagos ahí.
              </p>
              <Link
                to="/cobros"
                onClick={onClose}
                className="mt-3 inline-flex items-center justify-center rounded-lg bg-[#1A56DB] px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                Abrir Cobros y Créditos
              </Link>
            </div>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-[10px] border border-gray-300 py-3 text-[15px] font-medium text-gray-600 disabled:opacity-50"
                disabled={pdfShareBusy || !facturaCreadaParaPdf}
                onClick={() => void handleDescargarPdfCreada()}
              >
                {pdfShareBusy ? 'Generando PDF…' : 'Descargar PDF'}
              </button>
              <button
                type="button"
                className={`flex-1 rounded-[10px] border py-3 text-[15px] font-medium disabled:opacity-50 ${
                  sharePdfAvailable
                    ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : 'border-gray-200 bg-gray-50 text-gray-400'
                }`}
                disabled={pdfShareBusy || !facturaCreadaParaPdf}
                title={
                  sharePdfAvailable
                    ? 'Compartir PDF (AirDrop, Mail…)'
                    : 'Compartir solo en Safari (Mac/iPhone)'
                }
                onClick={() => void handleCompartirPdfCreada()}
              >
                Compartir PDF
              </button>
            </div>
            <button
              type="button"
              className="w-full rounded-[10px] py-3 text-[15px] font-medium text-white"
              style={{ backgroundColor: C.blue }}
              onClick={isEditMode ? onClose : handleNueva}
            >
              {isEditMode
                ? 'Cerrar'
                : wizardContext === 'pedidos'
                  ? 'Nuevo pedido'
                  : 'Nueva factura'}
            </button>
          </div>
          <button type="button" className="mt-4 text-sm text-gray-500 hover:text-gray-700" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center p-0 sm:p-4">
      <div className="flex h-[min(92vh,800px)] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-xl sm:max-h-[90vh] sm:rounded-2xl">
        {/* Top */}
        <div className="flex items-center border-b border-gray-200 px-4 py-3">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-full"
            style={{ backgroundColor: C.surface }}
            onClick={step === 0 ? onClose : goBack}
            aria-label={step === 0 ? 'Cerrar' : 'Atrás'}
          >
            <span className="text-gray-600">{step === 0 ? '✕' : '←'}</span>
          </button>
          <h1 className="ml-2 flex-1 text-[15px] font-medium text-gray-900">
            {isEditMode
              ? 'Editar factura'
              : wizardContext === 'pedidos'
                ? 'Nuevo pedido'
                : 'Nueva factura'}
          </h1>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={clsx(
                  'h-1.5 rounded-full transition-all',
                  i < step && 'w-1.5',
                  i === step && 'w-5',
                  i > step && 'w-1.5'
                )}
                style={{
                  backgroundColor: i < step ? C.green : i === step ? C.blue : C.border,
                }}
              />
            ))}
          </div>
        </div>
        <div className="h-0.5 w-full bg-gray-200">
          <div
            className="h-full transition-all"
            style={{
              width: `${((step + 1) / TOTAL_STEPS) * 100}%`,
              backgroundColor: C.blue,
            }}
          />
        </div>

        <div className="px-4 pt-3.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.blue }}>
            Paso {step + 1} de {TOTAL_STEPS}
          </p>
          <p className="text-xl font-medium text-gray-900">{stepTitles[step] ?? ''}</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          {step === 0 && (
            <WebStepPedido
              form={form}
              totales={totales}
              clienteSearch={clienteSearch}
              setClienteSearch={setClienteSearch}
              showClienteHits={showClienteHits}
              setShowClienteHits={setShowClienteHits}
              filtrados={filtrados}
              setCliente={setCliente}
              setNumeroFactura={setNumeroFactura}
              removeProducto={removeProducto}
              updateQty={updateQty}
              setDescuento={setDescuento}
              setDescuentoValor={setDescuentoValor}
              showProductos={showProductos}
              setShowProductos={setShowProductos}
              productos={productos}
              findByBarcode={findByBarcode}
              onPickProducto={onPickProducto}
              empresaId={currentEmpresa?.id ?? ''}
              createCliente={createCliente}
              updateCliente={updateCliente}
              feEmpresaActiva={feEmpresaActiva}
              emitirFacturaElectronica={emitirFacturaElectronica}
              setEmitirFacturaElectronica={setEmitirFacturaElectronica}
            />
          )}
          {step === 1 && (
            <WebStepEntrega
              form={form}
              invoicePaymentMethods={invoiceMethods}
              empresaId={currentEmpresa?.id ?? ''}
              totalFactura={totales.total}
              montoPagado={montoPagado}
              setMontoPagado={setMontoPagado}
              creditoModo={creditoModo}
              setCreditoModo={setCreditoModo}
              fechaVencSimple={fechaVencSimple}
              setFechaVencSimple={setFechaVencSimple}
              numCuotas={numCuotas}
              setNumCuotas={setNumCuotas}
              frecuenciaDias={frecuenciaDias}
              setFrecuenciaDias={setFrecuenciaDias}
              primeraFechaPlan={primeraFechaPlan}
              setPrimeraFechaPlan={setPrimeraFechaPlan}
              mensajeRegalo={mensajeRegalo}
              setMensajeRegalo={setMensajeRegalo}
              direccionRegalo={direccionRegalo}
              setDireccionRegalo={setDireccionRegalo}
              fechaAgendada={fechaAgendada}
              setFechaAgendada={setFechaAgendada}
              setTipoEntrega={setTipoEntrega}
              setCostoEnvio={setCostoEnvio}
              setMetodoEntrega={setMetodoEntrega}
              setNotasEntrega={setNotasEntrega}
              setCobrarEnvioAlDestino={setCobrarEnvioAlDestino}
              setFechaEntrega={setFechaEntrega}
              setDireccionEntrega={setDireccionEntrega}
              setMetodoPago={setMetodoPago}
              setAgendarCalendario={setAgendarCalendario}
              toggleRegalo={toggleRegalo}
              setNotas={setNotas}
              setCreditoAjusteTipo={setCreditoAjusteTipo}
              setCreditoAjusteValor={setCreditoAjusteValor}
              setCreditoAjusteSentido={setCreditoAjusteSentido}
              ajusteCredito={totales.ajuste_credito}
              comprobanteFile={comprobanteFile}
              setComprobanteFile={setComprobanteFile}
              comprobantePreview={comprobantePreview}
              setComprobantePreview={setComprobantePreview}
              fileInputRef={fileInputRef}
              wizardContext={wizardContext}
            />
          )}
          {step === 2 && (
            <WebStepConfirmar
              form={form}
              totales={totales}
              metodoPagoLabel={metodoPagoResumen}
              montoPagado={Math.max(0, Math.min(montoPagado, totales.total))}
              mensajeRegalo={mensajeRegalo}
              direccionRegalo={direccionRegalo}
              fechaAgendada={fechaAgendada}
              esCredito={confirmarEsCredito}
              creditoModo={creditoModo}
              fechaVencSimple={fechaVencSimple}
              numCuotas={numCuotas}
              frecuenciaDias={frecuenciaDias}
              primeraFechaPlan={primeraFechaPlan}
            />
          )}
        </div>

        <div className="flex gap-2 border-t border-gray-200 bg-white p-4">
          <button
            type="button"
            className="flex-1 rounded-[10px] border border-gray-300 py-3.5 text-[15px] font-medium text-gray-600"
            onClick={onClose}
          >
            Cancelar
          </button>
          {step < TOTAL_STEPS - 1 ? (
            <button
              type="button"
              disabled={!canAdvance || submitting || uploadingComprobante}
              className="flex-[2] rounded-[10px] py-3.5 text-[15px] font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: C.blue }}
              onClick={() => void handleNext(false)}
            >
              {submitting || uploadingComprobante
                ? uploadingComprobante
                  ? 'Subiendo comprobante…'
                  : 'Guardando…'
                : 'Siguiente →'}
            </button>
          ) : isEditMode ? (
            <button
              type="button"
              disabled={!canAdvance || submitting || uploadingComprobante}
              className="flex-[2] rounded-[10px] py-3.5 text-[15px] font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: C.blue }}
              onClick={() => void handleNext(false)}
            >
              {submitting || uploadingComprobante
                ? uploadingComprobante
                  ? 'Subiendo comprobante…'
                  : 'Guardando…'
                : 'Guardar cambios ✓'}
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={!canAdvance || submitting || uploadingComprobante}
                className="flex-1 rounded-[10px] border-2 py-3.5 text-[15px] font-medium disabled:opacity-50"
                style={{ borderColor: C.blue, color: C.blue, backgroundColor: 'white' }}
                onClick={() => void handleNext(false)}
              >
                {submitting || uploadingComprobante
                  ? uploadingComprobante
                    ? 'Subiendo…'
                    : 'Guardando…'
                  : 'Guardar pedido'}
              </button>
              <button
                type="button"
                disabled={!canAdvance || submitting || uploadingComprobante}
                className="flex-1 rounded-[10px] py-3.5 text-[15px] font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: C.blue }}
                onClick={() => void handleNext(true)}
              >
                {submitting || uploadingComprobante
                  ? uploadingComprobante
                    ? 'Subiendo…'
                    : 'Completando…'
                  : 'Completar venta ✓'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WebStepPedido({
  form,
  totales,
  clienteSearch,
  setClienteSearch,
  showClienteHits,
  setShowClienteHits,
  filtrados,
  setCliente,
  setNumeroFactura,
  removeProducto,
  updateQty,
  setDescuento,
  setDescuentoValor,
  showProductos,
  setShowProductos,
  productos,
  findByBarcode,
  onPickProducto,
  empresaId,
  createCliente,
  updateCliente,
  feEmpresaActiva,
  emitirFacturaElectronica,
  setEmitirFacturaElectronica,
}: {
  form: ReturnType<typeof useFacturaForm>['form'];
  totales: ReturnType<typeof useFacturaForm>['totales'];
  clienteSearch: string;
  setClienteSearch: (s: string) => void;
  showClienteHits: boolean;
  setShowClienteHits: (v: boolean) => void;
  filtrados: ClienteForm[];
  setCliente: (c: ClienteForm | null) => void;
  setNumeroFactura: (s: string) => void;
  removeProducto: (id: string) => void;
  updateQty: (id: string, d: number) => void;
  setDescuento: (t: 'ninguno' | 'porcentaje' | 'valor', v?: number) => void;
  setDescuentoValor: (n: number) => void;
  showProductos: boolean;
  setShowProductos: (v: boolean) => void;
  productos: Producto[];
  findByBarcode: (code: string) => Producto | undefined;
  onPickProducto: (p: Producto) => void;
  empresaId: string;
  createCliente: (
    cliente: Omit<import('../../types').Cliente, 'id' | 'createdAt' | 'updatedAt'>,
    empresaId: string
  ) => Promise<import('../../types').Cliente | null | void>;
  updateCliente: (
    id: string,
    updates: Partial<import('../../types').Cliente>,
    empresaId: string
  ) => Promise<unknown>;
  feEmpresaActiva: boolean;
  emitirFacturaElectronica: boolean;
  setEmitirFacturaElectronica: (v: boolean) => void;
}) {
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [savingCliente, setSavingCliente] = useState(false);
  const [quickForm, setQuickForm] = useState({
    nombre: '',
    telefono: '',
    tipoDocumento: 'CC',
    numeroDocumento: '',
    email: '',
  });
  const [feCompleteForm, setFeCompleteForm] = useState({
    tipoDocumento: 'CC',
    numeroDocumento: '',
    email: '',
  });

  const feMissing = emitirFacturaElectronica ? feMissingClientFields(form.cliente) : [];

  useEffect(() => {
    if (!form.cliente) return;
    setFeCompleteForm({
      tipoDocumento: form.cliente.tipoDocumento || 'CC',
      numeroDocumento: form.cliente.cedula || '',
      email: form.cliente.email || '',
    });
  }, [form.cliente?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const productosActivos = useMemo(
    () => productos.filter((p) => p.estado === 'ACTIVO'),
    [productos]
  );

  const productosFiltradosBusqueda = useMemo(() => {
    const q = productSearchTerm.trim().toLowerCase();
    if (!q) return productosActivos;
    return productosActivos.filter((p) => {
      const nombre = (p.nombre || '').toLowerCase();
      const desc = (p.descripcion || '').toLowerCase();
      const cat = (p.categoria || '').toLowerCase();
      const sku = (p.sku || '').toLowerCase();
      const bc = (p.barcode || '').toLowerCase();
      return (
        nombre.includes(q) ||
        desc.includes(q) ||
        cat.includes(q) ||
        sku.includes(q) ||
        bc.includes(q)
      );
    });
  }, [productosActivos, productSearchTerm]);

  const listaProductosModal = productSearchTerm.trim() ? productosFiltradosBusqueda : productosActivos.slice(0, 80);

  useEffect(() => {
    if (showProductos) setProductSearchTerm('');
  }, [showProductos]);

  const elegirProducto = useCallback(
    (p: Producto) => {
      setProductSearchTerm('');
      onPickProducto(p);
    },
    [onPickProducto]
  );

  const handleBarcodeScan = useCallback(
    (code: string) => {
      const producto = findByBarcode(code);
      if (!producto) {
        alert(`Producto no encontrado para el código: ${code}`);
        return;
      }
      if (producto.estado !== 'ACTIVO') {
        alert(`El producto "${producto.nombre}" no está activo.`);
        return;
      }
      elegirProducto(producto);
      setScannerOpen(false);
      setShowProductos(false);
    },
    [findByBarcode, elegirProducto, setShowProductos]
  );

  async function handleQuickCreate() {
    if (!empresaId) return;
    if (!quickForm.nombre.trim() || !quickForm.telefono.trim()) {
      alert('Nombre y teléfono son obligatorios.');
      return;
    }
    if (emitirFacturaElectronica) {
      const miss = feMissingClientFields({
        nombre: quickForm.nombre,
        email: quickForm.email,
        cedula: quickForm.numeroDocumento,
      });
      if (miss.length) {
        alert(`Para FE también necesitas: ${feMissingLabels(miss).join(', ')}.`);
        return;
      }
    }
    setSavingCliente(true);
    try {
      const created = await createCliente(
        {
          empresaId,
          nombre: quickForm.nombre.trim(),
          telefono: quickForm.telefono.trim(),
          tipoDocumento: (quickForm.tipoDocumento as 'CC') || 'CC',
          numeroDocumento: quickForm.numeroDocumento.trim() || undefined,
          email: quickForm.email.trim() || undefined,
          activo: true,
        },
        empresaId
      );
      if (!created?.id) throw new Error('No se pudo crear el cliente');
      const asForm: ClienteForm = {
        id: created.id,
        nombre: [created.nombre, created.apellido].filter(Boolean).join(' ').trim() || quickForm.nombre.trim(),
        telefono: created.telefono || quickForm.telefono.trim(),
        cedula: created.numeroDocumento || quickForm.numeroDocumento.trim() || undefined,
        email: created.email || quickForm.email.trim() || undefined,
        tipoDocumento: created.tipoDocumento || quickForm.tipoDocumento,
      };
      setCliente(asForm);
      setClienteSearch(asForm.nombre);
      setShowClienteHits(false);
      setShowQuickCreate(false);
      setQuickForm({
        nombre: '',
        telefono: '',
        tipoDocumento: 'CC',
        numeroDocumento: '',
        email: '',
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error al crear cliente');
    } finally {
      setSavingCliente(false);
    }
  }

  async function handleSaveFeFields() {
    if (!empresaId || !form.cliente?.id) return;
    if (!feCompleteForm.numeroDocumento.trim() || !feCompleteForm.email.trim()) {
      alert('Documento y email son obligatorios para FE.');
      return;
    }
    setSavingCliente(true);
    try {
      await updateCliente(
        form.cliente.id,
        {
          tipoDocumento: (feCompleteForm.tipoDocumento as 'CC') || 'CC',
          numeroDocumento: feCompleteForm.numeroDocumento.trim(),
          email: feCompleteForm.email.trim(),
        },
        empresaId
      );
      setCliente({
        ...form.cliente,
        tipoDocumento: feCompleteForm.tipoDocumento,
        cedula: feCompleteForm.numeroDocumento.trim(),
        email: feCompleteForm.email.trim(),
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'No se pudo guardar el cliente');
    } finally {
      setSavingCliente(false);
    }
  }

  return (
    <div className="space-y-4 pt-2">
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Cliente</p>
        <div className="flex gap-2">
          <input
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            placeholder="Buscar por nombre, teléfono o cédula…"
            value={clienteSearch}
            onChange={(e) => {
              const t = e.target.value;
              setClienteSearch(t);
              setShowClienteHits(t.length > 0);
              if (t === '') setCliente(null);
            }}
          />
          <button
            type="button"
            className="shrink-0 rounded-lg px-3.5 py-2.5 text-[13px] font-semibold text-white"
            style={{ backgroundColor: C.blue }}
            onClick={() => {
              setShowQuickCreate((v) => !v);
              setShowClienteHits(false);
              if (!quickForm.nombre && clienteSearch.trim()) {
                setQuickForm((f) => ({ ...f, nombre: clienteSearch.trim() }));
              }
            }}
          >
            + Nuevo
          </button>
        </div>

        {showQuickCreate && (
          <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-medium text-slate-800">Alta rápida de cliente</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input
                className="rounded-md border border-gray-200 px-3 py-2 text-sm sm:col-span-2"
                placeholder="Nombre *"
                value={quickForm.nombre}
                onChange={(e) => setQuickForm({ ...quickForm, nombre: e.target.value })}
              />
              <input
                className="rounded-md border border-gray-200 px-3 py-2 text-sm"
                placeholder="Teléfono *"
                value={quickForm.telefono}
                onChange={(e) => setQuickForm({ ...quickForm, telefono: e.target.value })}
              />
              <select
                className="rounded-md border border-gray-200 px-3 py-2 text-sm"
                value={quickForm.tipoDocumento}
                onChange={(e) => setQuickForm({ ...quickForm, tipoDocumento: e.target.value })}
              >
                <option value="CC">CC</option>
                <option value="CE">CE</option>
                <option value="TI">TI</option>
                <option value="PP">PP</option>
              </select>
              <input
                className="rounded-md border border-gray-200 px-3 py-2 text-sm"
                placeholder={emitirFacturaElectronica ? 'Cédula / documento *' : 'Cédula / documento'}
                value={quickForm.numeroDocumento}
                onChange={(e) => setQuickForm({ ...quickForm, numeroDocumento: e.target.value })}
              />
              <input
                className="rounded-md border border-gray-200 px-3 py-2 text-sm sm:col-span-2"
                placeholder={emitirFacturaElectronica ? 'Email *' : 'Email'}
                type="email"
                value={quickForm.email}
                onChange={(e) => setQuickForm({ ...quickForm, email: e.target.value })}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                disabled={savingCliente}
                onClick={() => void handleQuickCreate()}
                className="rounded-md px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: C.blue }}
              >
                {savingCliente ? 'Guardando…' : 'Crear y seleccionar'}
              </button>
              <button
                type="button"
                className="rounded-md border border-gray-200 px-3 py-2 text-sm"
                onClick={() => setShowQuickCreate(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {showClienteHits && filtrados.length > 0 && (
          <ul className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white">
            {filtrados.slice(0, 8).map((c) => (
              <li key={c.id} className="border-b border-gray-100 last:border-0">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50"
                  onClick={() => {
                    setCliente(c);
                    setClienteSearch(c.nombre);
                    setShowClienteHits(false);
                    setShowQuickCreate(false);
                  }}
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                    style={{ background: '#CECBF6', color: '#3C3489' }}
                  >
                    {c.nombre
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{c.nombre}</span>
                    <span className="text-xs text-gray-500">
                      {c.telefono || '—'} · {c.cedula || '—'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {feEmpresaActiva && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={emitirFacturaElectronica}
              onChange={(e) => setEmitirFacturaElectronica(e.target.checked)}
            />
            <span>
              <span className="font-medium">Facturación electrónica (DIAN)</span>
              <span className="block text-xs text-slate-500">
                Emite la FEV con Matias al crear el pedido. Requiere documento y email del cliente.
              </span>
            </span>
          </label>
          {emitirFacturaElectronica && feMissing.length > 0 && form.cliente && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
              <p className="text-xs font-medium text-amber-900">
                Falta para FE: {feMissingLabels(feMissing).join(', ')}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <select
                  className="rounded-md border border-amber-200 px-2 py-1.5 text-sm"
                  value={feCompleteForm.tipoDocumento}
                  onChange={(e) =>
                    setFeCompleteForm({ ...feCompleteForm, tipoDocumento: e.target.value })
                  }
                >
                  <option value="CC">CC</option>
                  <option value="CE">CE</option>
                  <option value="TI">TI</option>
                  <option value="PP">PP</option>
                </select>
                <input
                  className="rounded-md border border-amber-200 px-2 py-1.5 text-sm"
                  placeholder="Documento *"
                  value={feCompleteForm.numeroDocumento}
                  onChange={(e) =>
                    setFeCompleteForm({ ...feCompleteForm, numeroDocumento: e.target.value })
                  }
                />
                <input
                  className="rounded-md border border-amber-200 px-2 py-1.5 text-sm"
                  placeholder="Email *"
                  type="email"
                  value={feCompleteForm.email}
                  onChange={(e) => setFeCompleteForm({ ...feCompleteForm, email: e.target.value })}
                />
              </div>
              <button
                type="button"
                disabled={savingCliente}
                onClick={() => void handleSaveFeFields()}
                className="rounded-md bg-amber-800 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              >
                Guardar en el cliente
              </button>
            </div>
          )}
          {emitirFacturaElectronica && !form.cliente && (
            <p className="text-xs text-amber-800">
              Selecciona o crea un cliente y completa documento + email.
            </p>
          )}
        </div>
      )}

      {form.cliente && (
        <div
          className="flex items-center gap-3 rounded-lg border border-gray-200 p-2.5"
          style={{ backgroundColor: C.surface }}
        >
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
            style={{ background: '#CECBF6', color: '#3C3489' }}
          >
            {form.cliente.nombre
              .split(' ')
              .map((w) => w[0])
              .slice(0, 2)
              .join('')}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{form.cliente.nombre}</p>
            <p className="text-xs text-gray-500">
              {form.cliente.telefono || '—'} · {form.cliente.cedula || '—'}
            </p>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600"
            onClick={() => {
              setCliente(null);
              setClienteSearch('');
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase text-gray-500">N° Factura</label>
          <input
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
            value={form.numero_factura}
            onChange={(e) => setNumeroFactura(e.target.value)}
          />
        </div>
      </div>

      <hr className="border-gray-200" />

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Productos y servicios</p>
          <button
            type="button"
            className="text-sm font-medium"
            style={{ color: C.blue }}
            onClick={() => setShowProductos(true)}
          >
            + Agregar
          </button>
        </div>
        {form.productos.length === 0 && (
          <p className="py-4 text-center text-sm text-gray-400">Aún no hay productos. Agrega uno.</p>
        )}
        {form.productos.map((p) => (
          <div
            key={p.id}
            className="mb-2 flex items-center gap-2 rounded-lg p-2.5"
            style={{ backgroundColor: C.surface }}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-gray-900">{p.nombre}</p>
              <p className="text-[11px] text-gray-500">{fmtCop(p.precio)} c/u</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700"
                onClick={() => updateQty(p.id, -1)}
              >
                −
              </button>
              <span className="w-5 text-center text-sm font-medium">{p.qty}</span>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700"
                onClick={() => updateQty(p.id, 1)}
              >
                +
              </button>
            </div>
            <span className="min-w-[4.5rem] text-right text-[13px] font-medium">{fmtCop(p.precio * p.qty)}</span>
            <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => removeProducto(p.id)}>
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="mt-1 w-full rounded-lg border border-dashed border-gray-300 py-2.5 text-[13px] font-medium"
          style={{ color: C.blue }}
          onClick={() => setShowProductos(true)}
        >
          + Agregar producto / servicio
        </button>
      </div>

      {form.productos.length > 0 && (
        <>
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase text-gray-500">Descuento</p>
            <div className="flex gap-2">
              {(
                [
                  ['ninguno', 'Sin descuento'],
                  ['porcentaje', '% Porcentaje'],
                  ['valor', 'Valor fijo'],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className={clsx(
                    'flex-1 rounded-lg border py-2 text-xs font-medium',
                    form.descuento_tipo === k ? 'border-[#185FA5] bg-[#E6F1FB] text-[#185FA5]' : 'border-gray-200 text-gray-600'
                  )}
                  onClick={() => setDescuento(k)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {form.descuento_tipo !== 'ninguno' && (
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase text-gray-500">
                {form.descuento_tipo === 'porcentaje' ? '% Descuento' : 'Valor (COP)'}
              </label>
              <input
                type="number"
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
                placeholder="0"
                value={form.descuento_valor > 0 ? String(form.descuento_valor) : ''}
                onChange={(e) => setDescuentoValor(Number(e.target.value) || 0)}
              />
            </div>
          )}
          <TotalsBox totales={totales} />
        </>
      )}

      {showProductos && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 sm:items-center p-4">
          <div className="flex max-h-[min(85vh,640px)] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <span className="font-medium text-gray-900">Productos y servicios</span>
              <button
                type="button"
                onClick={() => setShowProductos(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="shrink-0 space-y-3 border-b border-gray-100 bg-white px-4 py-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex flex-1 min-w-[10rem] items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-[13px] font-semibold text-white sm:flex-none"
                  style={{ backgroundColor: C.blue }}
                  onClick={() => document.getElementById('nf-buscar-producto')?.focus()}
                >
                  <PlusIcon className="h-4 w-4 shrink-0" />
                  Agregar Producto/Servicio
                </button>
                <button
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  className="inline-flex flex-1 min-w-[10rem] items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#185FA5] bg-white px-3 py-2.5 text-[13px] font-semibold text-[#185FA5] hover:bg-blue-50 sm:flex-none"
                >
                  <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                    />
                  </svg>
                  Escanear código
                </button>
              </div>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden />
                </div>
                <input
                  id="nf-buscar-producto"
                  type="search"
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, descripción o categoría..."
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-[#185FA5] focus:outline-none focus:ring-2 focus:ring-[#185FA5]/30"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500">
                {productSearchTerm.trim()
                  ? `Mostrando ${listaProductosModal.length} resultado(s)`
                  : 'Escribe para filtrar o elige un producto de la lista'}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {listaProductosModal.length > 0 ? (
                <div className="space-y-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
                  {listaProductosModal.map((p) => {
                    const ivaPct = Number(p.ivaPorcentaje) || 0;
                    const sinIva = precioSinIvaMostrar(p);
                    const thumb = (Array.isArray(p.imagenes) && p.imagenes[0]) || p.imagen || null;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className="flex w-full items-start justify-between gap-3 border-b border-gray-100 p-3 text-left last:border-b-0 hover:bg-gray-50"
                        onClick={() => elegirProducto(p)}
                      >
                        {thumb ? (
                          <img
                            src={thumb}
                            alt=""
                            loading="lazy"
                            className="h-12 w-12 shrink-0 rounded-md border border-gray-200 object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-sm font-semibold text-gray-400">
                            {(p.nombre || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900">{p.nombre}</p>
                          {p.descripcion ? (
                            <p className="mt-0.5 line-clamp-2 text-xs text-gray-600">{p.descripcion}</p>
                          ) : null}
                          <p className="mt-1 text-[11px] text-gray-500">
                            {[
                              tipoOfertaLabel(p),
                              p.sku ? `SKU ${p.sku}` : null,
                              p.categoria || '—',
                              stockEtiqueta(p),
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-sm font-semibold text-gray-900">{fmtCop(Number(p.precioVenta) || 0)}</p>
                          <p className="text-[11px] text-gray-500">IVA: {ivaPct}%</p>
                          {ivaPct > 0 && sinIva != null ? (
                            <p className="text-[11px] text-gray-500">Sin IVA: {fmtCop(sinIva)}</p>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 py-8 text-center text-sm text-gray-500">
                  {productosActivos.length === 0
                    ? 'No hay productos activos. Crea productos en el módulo Productos.'
                    : 'No hay coincidencias. Prueba otro término o escanea el código.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {scannerOpen && (
        <BarcodeScanner
          overlayClassName="z-[110]"
          onScan={handleBarcodeScan}
          onClose={() => setScannerOpen(false)}
          placeholder="Código de barras o SKU"
        />
      )}
    </div>
  );
}

function WebStepEntrega({
  form,
  invoicePaymentMethods,
  empresaId,
  totalFactura,
  montoPagado,
  setMontoPagado,
  creditoModo,
  setCreditoModo,
  fechaVencSimple,
  setFechaVencSimple,
  numCuotas,
  setNumCuotas,
  frecuenciaDias,
  setFrecuenciaDias,
  primeraFechaPlan,
  setPrimeraFechaPlan,
  mensajeRegalo,
  setMensajeRegalo,
  direccionRegalo,
  setDireccionRegalo,
  fechaAgendada,
  setFechaAgendada,
  setTipoEntrega,
  setCostoEnvio,
  setMetodoEntrega,
  setNotasEntrega,
  setCobrarEnvioAlDestino,
  setFechaEntrega,
  setDireccionEntrega,
  setMetodoPago,
  setAgendarCalendario,
  toggleRegalo,
  setNotas,
  setCreditoAjusteTipo,
  setCreditoAjusteValor,
  setCreditoAjusteSentido,
  ajusteCredito,
  comprobanteFile,
  setComprobanteFile,
  comprobantePreview,
  setComprobantePreview,
  fileInputRef,
  wizardContext = 'pedidos',
}: {
  form: ReturnType<typeof useFacturaForm>['form'];
  /** `payment_methods` activos con «Mostrar en facturas» (Configuración). */
  invoicePaymentMethods: PaymentMethod[];
  empresaId: string;
  totalFactura: number;
  montoPagado: number;
  setMontoPagado: (n: number) => void;
  creditoModo: 'simple' | 'plan_pagos';
  setCreditoModo: (m: 'simple' | 'plan_pagos') => void;
  fechaVencSimple: string;
  setFechaVencSimple: (s: string) => void;
  numCuotas: number;
  setNumCuotas: (n: number) => void;
  frecuenciaDias: 7 | 15 | 30;
  setFrecuenciaDias: (d: 7 | 15 | 30) => void;
  primeraFechaPlan: string;
  setPrimeraFechaPlan: (s: string) => void;
  mensajeRegalo: string;
  setMensajeRegalo: (s: string) => void;
  direccionRegalo: string;
  setDireccionRegalo: (s: string) => void;
  fechaAgendada: string;
  setFechaAgendada: (s: string) => void;
  setTipoEntrega: (t: 'tienda' | 'domicilio') => void;
  setCostoEnvio: (n: number) => void;
  setMetodoEntrega: (m: 'recogida' | 'entrega_local' | 'envio') => void;
  setNotasEntrega: (s: string) => void;
  setCobrarEnvioAlDestino: (v: boolean) => void;
  setFechaEntrega: (s: string) => void;
  setDireccionEntrega: (s: string) => void;
  setMetodoPago: (m: string) => void;
  setAgendarCalendario: (v: boolean) => void;
  toggleRegalo: () => void;
  setNotas: (s: string) => void;
  setCreditoAjusteTipo: (t: 'ninguno' | 'porcentaje' | 'valor') => void;
  setCreditoAjusteValor: (n: number) => void;
  setCreditoAjusteSentido: (s: 'descuento' | 'recargo') => void;
  ajusteCredito: number;
  comprobanteFile: File | null;
  setComprobanteFile: (f: File | null) => void;
  comprobantePreview: string | null;
  setComprobantePreview: (s: string | null) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null> | React.RefObject<HTMLInputElement>;
  wizardContext?: NuevaFacturaWizardContext;
}) {
  const parsedAgenda = splitAgendaLocal(fechaAgendada);
  const [agendaPickDate, setAgendaPickDate] = useState<string | null>(parsedAgenda.date);
  const [agendaPickTime, setAgendaPickTime] = useState<string | null>(parsedAgenda.time);

  useEffect(() => {
    const p = splitAgendaLocal(fechaAgendada);
    setAgendaPickDate(p.date);
    setAgendaPickTime(p.time);
  }, [fechaAgendada]);

  const onAgendaCalendarSelect = (date: string, time: string) => {
    if (time) {
      setAgendaPickDate(date);
      setAgendaPickTime(time);
      setFechaAgendada(`${date}T${time}:00`);
      setFechaEntrega(date);
    } else {
      setAgendaPickDate(date);
      setAgendaPickTime(null);
      setFechaAgendada('');
    }
  };

  const [montoPagadoDraft, setMontoPagadoDraft] = useState(() =>
    montoPagado === 0 ? '' : String(Math.round(montoPagado)),
  );

  useEffect(() => {
    setMontoPagadoDraft(montoPagado === 0 ? '' : String(Math.round(montoPagado)));
  }, [montoPagado]);

  /** Valor tecleado: `Math.max(2, dígito)` impedía escribir 10-36 (el primer «1» se volvía 2 al instante). */
  const [numCuotasInput, setNumCuotasInput] = useState(() => String(numCuotas));
  useEffect(() => {
    setNumCuotasInput(String(numCuotas));
  }, [numCuotas]);

  const metodoSistemaRow = useMemo(
    () => invoicePaymentMethods.find((m) => m.id === METODO_PAGO_SISTEMA_CREDITO_ID),
    [invoicePaymentMethods]
  );
  const metodosPagoInmediato = useMemo(
    () => invoicePaymentMethods.filter((m) => m.id !== METODO_PAGO_SISTEMA_CREDITO_ID),
    [invoicePaymentMethods]
  );

  const selectedMethod = invoicePaymentMethods.find((m) => m.id === form.metodo_pago);
  const esCredito =
    form.metodo_pago === METODO_PAGO_SISTEMA_CREDITO_ID ||
    Boolean(
      selectedMethod &&
        isCreditoPaymentMethod({
          method_type: selectedMethod.method_type,
          method_name: selectedMethod.method_name,
        })
    );
  const hasConfiguredMethods = invoicePaymentMethods.length > 0;
  const esPedidos = wizardContext === 'pedidos';
  const saldoPendiente = Math.max(0, totalFactura - montoPagado);
  const estadoPagoUi =
    montoPagado <= 0 ? 'PENDIENTE' : saldoPendiente <= 0 ? 'PAGADO' : 'PAGO_PARCIAL';

  const onPickMethod = (methodId: string) => {
    setMetodoPago(methodId);
  };

  return (
    <div className="space-y-4 pt-2">
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase text-gray-500">Forma de pago</p>
        {metodosPagoInmediato.length === 0 ? (
          <p className="mb-2 rounded-lg bg-amber-50 p-3 text-[13px] text-amber-900">
            No hay otros métodos con «Mostrar en facturas». Agrégalos en{' '}
            <strong className="font-semibold">Configuración → Métodos de pago</strong>.{' '}
            <strong>Crédito</strong> siempre está disponible abajo.
          </p>
        ) : null}
        {hasConfiguredMethods && esPedidos ? (
          <>
            <div className="mb-3 rounded-lg border border-[#0B1F4A]/20 bg-[#0B1F4A]/[0.05] p-3 text-[13px] text-[#0B1F4A]">
              <p className="font-semibold">Qué ofrecer al cliente</p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-gray-700">
                <li>
                  <strong>Crédito</strong> (saldo pendiente): se registra en <Link to="/cobros" className="font-medium text-[#1A56DB] underline">Cobros y Créditos</Link> al crear el pedido.
                </li>
                <li>
                  Indica <strong>abono hoy</strong> abajo o deja 0; el resto queda por cobrar.
                </li>
                <li>
                  <strong>Pago hoy</strong>: efectivo, transferencia, etc., en la segunda fila.
                </li>
              </ul>
            </div>
            {metodoSistemaRow && (
              <div className="mb-4">
                <p className="mb-2 text-[11px] font-semibold uppercase text-[#0B1F4A]">
                  Crédito al cliente
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <button
                    key={metodoSistemaRow.id}
                    type="button"
                    onClick={() => onPickMethod(metodoSistemaRow.id)}
                    className={clsx(
                      'rounded-lg border p-3 text-center text-sm transition-all',
                      form.metodo_pago === metodoSistemaRow.id
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <span className="block text-lg">🧾</span>
                    <span>Crédito</span>
                  </button>
                </div>
              </div>
            )}
            {metodosPagoInmediato.length > 0 && (
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase text-gray-500">
                  Pago hoy
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {metodosPagoInmediato.map((method) => {
                    const icono =
                      (method.icon && String(method.icon).trim()) || iconForPaymentMethodType(method.method_type);
                    const nombre = method.method_name?.trim() || method.method_type;
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => onPickMethod(method.id)}
                        className={clsx(
                          'rounded-lg border p-3 text-center text-sm transition-all',
                          form.metodo_pago === method.id
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <span className="block text-lg">{icono}</span>
                        <span>{nombre}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : hasConfiguredMethods ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {metodosPagoInmediato.map((method) => {
            const icono =
              (method.icon && String(method.icon).trim()) || iconForPaymentMethodType(method.method_type);
            const nombre = method.method_name?.trim() || method.method_type;
            return (
              <button
                key={method.id}
                type="button"
                onClick={() => onPickMethod(method.id)}
                className={clsx(
                  'rounded-lg border p-3 text-center text-sm transition-all',
                  form.metodo_pago === method.id
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <span className="block text-lg">{icono}</span>
                <span>{nombre}</span>
              </button>
            );
          })}
          {metodoSistemaRow ? (
            <button
              key={metodoSistemaRow.id}
              type="button"
              onClick={() => onPickMethod(metodoSistemaRow.id)}
              className={clsx(
                'rounded-lg border p-3 text-center text-sm transition-all',
                form.metodo_pago === metodoSistemaRow.id
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <span className="block text-lg">🧾</span>
              <span>Crédito</span>
            </button>
          ) : null}
        </div>
        ) : null}

        {selectedMethod && !esCredito ? (
          <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700 space-y-2">
            {selectedMethod.description?.trim() ? (
              <p className="text-gray-700">{selectedMethod.description.trim()}</p>
            ) : null}
            {(() => {
              const acc = (selectedMethod.account_info || {}) as Record<string, unknown>;
              const qrUrl = getPaymentMethodQrUrl(acc);
              const fields = getPaymentMethodDisplayFields(acc);
              return (
                <>
                  {qrUrl ? (
                    <div className="flex flex-col items-center gap-2 py-1">
                      <p className="text-xs text-gray-600">Escanea el código para pagar</p>
                      <img
                        src={qrUrl}
                        alt="Código QR de pago"
                        className="h-48 w-48 max-w-full rounded border border-gray-200 bg-white object-contain p-2"
                      />
                    </div>
                  ) : null}
                  {fields.length > 0 ? (
                    <ul className="text-xs space-y-1 text-gray-600">
                      {fields.map((f) => (
                        <li key={`${f.label}-${f.value}`}>
                          <span className="font-medium text-gray-700">{f.label}:</span>{' '}
                          <PaymentMethodFieldValue field={f} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              );
            })()}
          </div>
        ) : null}
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700">Monto pagado (COP)</label>
        <p className="text-xs text-gray-400">Deja vacío o 0 si es a crédito o se paga después</p>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={montoPagadoDraft}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, '');
            setMontoPagadoDraft(raw);
            if (raw === '') {
              setMontoPagado(0);
              return;
            }
            const n = Number(raw);
            if (Number.isNaN(n)) return;
            const capped = Math.min(Math.max(0, n), totalFactura);
            setMontoPagado(capped);
            if (String(Math.round(capped)) !== raw) {
              setMontoPagadoDraft(String(Math.round(capped)));
            }
          }}
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
          placeholder="Ej: 50000"
        />
        {montoPagado < totalFactura ? (
          <p className="mt-1 text-xs font-medium text-amber-700">
            Saldo pendiente: ${Math.max(0, totalFactura - montoPagado).toLocaleString('es-CO')}
          </p>
        ) : null}

        <div className="text-sm space-y-1 p-3 bg-gray-50 rounded-lg mt-3">
          <div className="flex justify-between">
            <span className="text-gray-500">Total factura</span>
            <span className="font-medium">${Math.round(totalFactura).toLocaleString('es-CO')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Monto pagado</span>
            <span className="font-medium text-green-600">
              ${Math.round(montoPagado).toLocaleString('es-CO')}
            </span>
          </div>
          <div className="flex justify-between border-t pt-1">
            <span className="text-gray-500">Saldo pendiente</span>
            <span
              className={clsx('font-semibold', saldoPendiente > 0 ? 'text-amber-600' : 'text-green-600')}
            >
              ${Math.round(saldoPendiente).toLocaleString('es-CO')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Estado</span>
            <span
              className={clsx(
                'text-xs font-medium px-2 py-0.5 rounded-full',
                esCredito && saldoPendiente > 0
                  ? 'bg-blue-100 text-blue-700'
                  : estadoPagoUi === 'PAGADO'
                  ? 'bg-green-100 text-green-700'
                  : estadoPagoUi === 'PAGO_PARCIAL'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
              )}
            >
              {esCredito && saldoPendiente > 0
                ? '🧾 Crédito'
                : estadoPagoUi === 'PAGADO'
                  ? '✓ Pagada'
                  : estadoPagoUi === 'PAGO_PARCIAL'
                    ? '⚡ Parcial'
                    : '⏳ Pendiente'}
            </span>
          </div>
        </div>
      </div>

      {esCredito ? (
        <div className="mt-4 space-y-3 rounded-xl border border-[#0B1F4A]/25 bg-white p-3 shadow-sm">
          <div>
            <p className="text-[12px] font-semibold text-[#0B1F4A]">Fechas de cobro (no es la entrega del pedido)</p>
            <p className="text-xs text-gray-500">
              El calendario de <strong>cuotas</strong> queda en <strong>Cobros y Créditos</strong> (y en avisos si tu cuenta
              los tiene). En la sección <strong>Entrega</strong> (al final de este paso), si activas{' '}
              <strong>Programar en calendario</strong>, añadimos la
              <strong> cita de entrega o recogida</strong> al calendario del equipo.
            </p>
            <p className="text-xs text-gray-500">
              El saldo pendiente se define aquí; es distinto a la cita de entrega.
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase text-gray-500">Ajustar total</p>
            <p className="text-xs text-gray-500">Descuento o recargo sobre el total, en COP o en %.</p>
            <div className="flex gap-2">
              {(
                [
                  ['ninguno', 'Sin cambio'],
                  ['porcentaje', '%'],
                  ['valor', '$ COP'],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setCreditoAjusteTipo(k)}
                  className={clsx(
                    'flex-1 rounded-lg border py-2 text-[12px] font-semibold',
                    form.credito_ajuste_tipo === k
                      ? 'border-[#1A56DB] bg-[#1A56DB]/10 text-[#0B1F4A]'
                      : 'border-gray-200 text-gray-600'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {form.credito_ajuste_tipo !== 'ninguno' ? (
              <>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCreditoAjusteSentido('descuento')}
                    className={clsx(
                      'flex-1 rounded-lg border py-2 text-[12px] font-semibold',
                      form.credito_ajuste_sentido === 'descuento'
                        ? 'border-[#1A56DB] bg-[#1A56DB]/10 text-[#0B1F4A]'
                        : 'border-gray-200 text-gray-600'
                    )}
                  >
                    Descuento
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreditoAjusteSentido('recargo')}
                    className={clsx(
                      'flex-1 rounded-lg border py-2 text-[12px] font-semibold',
                      form.credito_ajuste_sentido === 'recargo'
                        ? 'border-[#1A56DB] bg-[#1A56DB]/10 text-[#0B1F4A]'
                        : 'border-gray-200 text-gray-600'
                    )}
                  >
                    Recargo
                  </button>
                </div>
                <label className="text-[11px] font-semibold uppercase text-gray-500">
                  {form.credito_ajuste_tipo === 'porcentaje' ? '% a aplicar' : 'Valor (COP)'}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={form.credito_ajuste_valor > 0 ? String(form.credito_ajuste_valor) : ''}
                  onChange={(e) => {
                    const n = Number(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                    setCreditoAjusteValor(
                      form.credito_ajuste_tipo === 'porcentaje' ? Math.min(100, n) : n
                    );
                  }}
                  placeholder={form.credito_ajuste_tipo === 'porcentaje' ? 'Ej: 10' : 'Ej: 15000'}
                />
                {form.credito_ajuste_tipo === 'porcentaje' ? (
                  <div className="flex flex-wrap gap-1.5">
                    {([5, 10, 15] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setCreditoAjusteValor(p)}
                        className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] text-gray-700"
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                ) : null}
                {ajusteCredito !== 0 ? (
                  <p className="text-[12px] font-semibold text-[#0B1F4A]">
                    {ajusteCredito > 0 ? 'Recargo' : 'Descuento'}: {ajusteCredito > 0 ? '+' : '−'}
                    {fmtCop(Math.abs(Math.round(ajusteCredito)))} → total {fmtCop(Math.round(totalFactura))}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>

          {saldoPendiente <= 0 ? (
            <p className="text-sm text-gray-600">Con el abono cubres el total; no aplica vencimiento de crédito.</p>
          ) : (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-sm text-amber-950">
                <span className="font-semibold">A cobrar después del abono:</span>{' '}
                <span className="font-bold">{fmtCop(Math.round(saldoPendiente))}</span>
                {creditoModo === 'simple' ? (
                  <span className="block text-[12px] font-medium text-amber-900/90">
                    Un pago completo a más tardar el <strong>{fechaVencSimple}</strong>
                  </span>
                ) : (
                  <span className="block text-[12px] text-amber-900/90">
                    En <strong>{Math.min(36, Math.max(2, Math.floor(numCuotas)))}</strong> cuotas (ver vista previa abajo).
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCreditoModo('simple')}
                  className={clsx(
                    'flex-1 rounded-lg border py-2 text-[12px] font-semibold',
                    creditoModo === 'simple'
                      ? 'border-[#1A56DB] bg-[#1A56DB]/10 text-[#0B1F4A]'
                      : 'border-gray-200 text-gray-600'
                  )}
                >
                  Un solo pago
                </button>
                <button
                  type="button"
                  onClick={() => setCreditoModo('plan_pagos')}
                  className={clsx(
                    'flex-1 rounded-lg border py-2 text-[12px] font-semibold',
                    creditoModo === 'plan_pagos'
                      ? 'border-[#1A56DB] bg-[#1A56DB]/10 text-[#0B1F4A]'
                      : 'border-gray-200 text-gray-600'
                  )}
                >
                  Plan de cuotas
                </button>
              </div>
              {creditoModo === 'simple' ? (
                <div className="space-y-2">
                  <label className="text-[11px] font-semibold uppercase text-gray-500">Fecha límite de pago</label>
                  <input
                    type="date"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    value={fechaVencSimple}
                    onChange={(e) => setFechaVencSimple(e.target.value)}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {(
                      [
                        { d: 7, t: '+7 días' },
                        { d: 15, t: '+15 días' },
                        { d: 30, t: '+30 días' },
                      ] as const
                    ).map((x) => (
                      <button
                        key={x.t}
                        type="button"
                        onClick={() => setFechaVencSimple(ymdAddDays(toYmdLocal(new Date()), x.d))}
                        className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] text-gray-700"
                      >
                        {x.t}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFechaVencSimple(ymdFinDeMesLocal())}
                      className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] text-gray-700"
                    >
                      Fin de mes
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                      <label className="text-[11px] font-semibold uppercase text-gray-500">Número de cuotas</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={numCuotasInput}
                        onChange={(e) => {
                          const t = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setNumCuotasInput(t);
                          if (t !== '') {
                            const n = parseInt(t, 10);
                            if (!Number.isNaN(n) && n >= 2 && n <= 36) {
                              setNumCuotas(n);
                            }
                          }
                        }}
                        onBlur={() => {
                          const n = parseInt(numCuotasInput, 10);
                          const v = Math.min(
                            36,
                            Math.max(2, numCuotasInput.trim() === '' || Number.isNaN(n) ? 2 : n),
                          );
                          setNumCuotas(v);
                          setNumCuotasInput(String(v));
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold uppercase text-gray-500">Primera cuota (vence)</label>
                      <input
                        type="date"
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={primeraFechaPlan}
                        onChange={(e) => setPrimeraFechaPlan(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-gray-500">Frecuencia entre cuotas</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(
                        [
                          { d: 7 as const, t: 'Semanal' },
                          { d: 15 as const, t: 'Quincenal' },
                          { d: 30 as const, t: 'Mensual' },
                        ] as const
                      ).map((x) => (
                        <button
                          key={x.d}
                          type="button"
                          onClick={() => setFrecuenciaDias(x.d)}
                          className={clsx(
                            'rounded-full border px-2.5 py-1 text-[11px]',
                            frecuenciaDias === x.d
                              ? 'border-[#1A56DB] bg-blue-50 font-semibold text-[#0B1F4A]'
                              : 'border-gray-200 bg-white text-gray-600'
                          )}
                        >
                          {x.t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 p-2">
                    <p className="text-[11px] font-semibold text-gray-600">
                      Vista previa — cuotas sobre el total ({fmtCop(Math.round(totalFactura))})
                    </p>
                    <p className="text-[10px] text-gray-500">
                      El abono de hoy ({fmtCop(Math.round(montoPagado))}) se refleja al guardar; saldo por cobrar:{' '}
                      {fmtCop(Math.round(saldoPendiente))}.
                    </p>
                    <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto text-[11px] text-gray-700">
                      {(() => {
                        const n = Math.min(36, Math.max(2, Math.floor(numCuotas)));
                        const parts = partesIgualesCOP(totalFactura, n);
                        const fechas = fechasPlanCuotas(primeraFechaPlan, n, frecuenciaDias);
                        return parts.map((m, i) => (
                          <li key={i} className="flex justify-between gap-2">
                            <span>Cuota {i + 1}</span>
                            <span className="text-gray-500">{fechas[i] || '—'}</span>
                            <span className="font-medium">{fmtCop(Math.round(m))}</span>
                          </li>
                        ));
                      })()}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {form.metodo_pago && (!esCredito || montoPagado > 0) ? (
        <div>
          <label className="text-sm font-medium text-gray-700">
            {esCredito && montoPagado > 0 ? (
              <>
                Comprobante del abono <span className="text-gray-400">(recomendado)</span>
              </>
            ) : (
              <>
                Comprobante de pago <span className="text-gray-400">(opcional)</span>
              </>
            )}
          </label>
          {esCredito && montoPagado > 0 ? (
            <p className="text-xs text-gray-500">
              Sube foto o PDF del abono de hoy (transferencia, consignación, etc.).
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-1 w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 cursor-pointer text-center"
          >
            {comprobantePreview && comprobanteFile?.type.startsWith('image/') ? (
              <img src={comprobantePreview} alt="" className="max-h-32 mx-auto rounded" />
            ) : comprobanteFile ? (
              <p className="text-sm text-gray-600">{comprobanteFile.name}</p>
            ) : (
              <>
                <ArrowUpTrayIcon className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                <p className="text-sm text-gray-500">Toca para subir comprobante</p>
              </>
            )}
          </button>
          <input
            ref={fileInputRef as React.Ref<HTMLInputElement>}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setComprobanteFile(file);
                setComprobantePreview(URL.createObjectURL(file));
              }
              e.target.value = '';
            }}
          />
        </div>
      ) : null}

      {esCredito ? (
        <div className="mt-2 border-t border-gray-100 pt-3">
          <p className="text-[11px] font-semibold text-gray-500">Referencia: otros créditos del cliente</p>
          <p className="text-[10px] text-gray-500">
            No confundir con el plan de <strong>esta</strong> venta (arriba). Es solo el historial en Cobros y Créditos.
          </p>
          {empresaId && form.cliente?.id ? (
            <div className="mt-2 rounded-lg border border-gray-200 bg-white p-2">
              <ClienteCreditosTab empresaId={empresaId} clienteId={form.cliente.id} layout="compact" />
            </div>
          ) : (
            <p className="mt-1 text-[11px] text-amber-800">Sin cliente no hay referencia de créditos anteriores.</p>
          )}
        </div>
      ) : null}

      <hr className="border-gray-200" />
      <div className="space-y-3 rounded-[10px] px-3 py-3" style={{ backgroundColor: C.surface }}>
        <p className="text-[11px] font-semibold uppercase text-gray-500">Entrega</p>
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase text-gray-500">Tipo de entrega</p>
          <div className="flex gap-2">
            <button
              type="button"
              className={clsx(
                'flex-1 rounded-lg border p-2.5 text-center text-[11px]',
                form.tipo_entrega === 'tienda' ? 'border-[#185FA5] bg-[#E6F1FB] font-semibold text-[#185FA5]' : 'border-gray-200 text-gray-600'
              )}
              onClick={() => setTipoEntrega('tienda')}
            >
              <span className="text-lg">🏪</span>
              <br />
              En tienda
            </button>
            <button
              type="button"
              className={clsx(
                'flex-1 rounded-lg border p-2.5 text-center text-[11px]',
                form.tipo_entrega === 'domicilio' ? 'border-[#185FA5] bg-[#E6F1FB] font-semibold text-[#185FA5]' : 'border-gray-200 text-gray-600'
              )}
              onClick={() => setTipoEntrega('domicilio')}
            >
              <span className="text-lg">🚚</span>
              <br />
              Domicilio
            </button>
          </div>
        </div>
        {form.tipo_entrega === 'domicilio' && (
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase text-gray-500">Dirección de entrega</label>
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
              value={form.direccion_entrega || ''}
              onChange={(e) => setDireccionEntrega(e.target.value)}
              placeholder="Cra 45 #23-10"
            />
          </div>
        )}

        <div className="space-y-2 border-t border-gray-200 pt-3">
          <p className="text-[11px] font-semibold uppercase text-gray-500">Modalidad y envío</p>
          <select
            className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900"
            value={form.metodo_entrega}
            onChange={(e) =>
              setMetodoEntrega(e.target.value as 'recogida' | 'entrega_local' | 'envio')
            }
          >
            <option value="recogida">Recogida en tienda</option>
            <option value="entrega_local">Entrega local (reparto propio)</option>
            <option value="envio">Envío / paquetería</option>
          </select>
          {(form.metodo_entrega === 'entrega_local' || form.metodo_entrega === 'envio') && (
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase text-gray-500">
                Costo de envío (COP, sin decimales)
              </label>
              <input
                type="number"
                min={0}
                step={1}
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
                value={form.costo_envio || ''}
                onChange={(e) => setCostoEnvio(Math.max(0, Math.round(Number(e.target.value) || 0)))}
                placeholder="0"
              />
            </div>
          )}
          {(form.metodo_entrega === 'entrega_local' || form.metodo_entrega === 'envio') && (
            <label className="mt-2 flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300"
                checked={form.cobrar_envio_al_destino}
                onChange={(e) => setCobrarEnvioAlDestino(e.target.checked)}
              />
              <span>
                <strong>Cobrar envío al destino</strong>
                <span className="block text-xs text-gray-600">
                  El costo de envío no se suma al total de la factura; el mensajero lo cobra al entregar.
                </span>
              </span>
            </label>
          )}
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase text-gray-500">Notas de entrega</label>
            <textarea
              className="w-full min-h-[72px] rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={form.notas_entrega || ''}
              onChange={(e) => setNotasEntrega(e.target.value)}
              placeholder="Indicaciones para el mensajero, ventana horaria, etc."
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-2.5">
          <ToggleRow
            label="Programar en calendario"
            sub="Reservar fecha y hora de entrega o recogida (opcional). Solo al activarlo se crea la cita en el calendario."
            on={form.agendar_calendario}
            onToggle={() => {
              const next = !form.agendar_calendario;
              setAgendarCalendario(next);
              if (!next) {
                setFechaAgendada('');
                setAgendaPickDate(null);
                setAgendaPickTime(null);
              }
            }}
            hideSeparator
          />
        </div>
        {form.agendar_calendario && (
          <div>
            <div className="rounded-xl border border-[#185FA5]/30 bg-gradient-to-b from-[#E6F1FB]/60 to-white p-3">
              <p className="text-[12px] font-semibold text-[#0B1F4A]">
                {form.tipo_entrega === 'domicilio' ? 'Entrega a domicilio' : 'Recogida en tienda'}
              </p>
              <p className="text-xs text-gray-600">
                Al elegir <strong>fecha y hora</strong> se guarda en el pedido y se sincroniza con el calendario del
                equipo (mismo tipo: domicilio o recogida).
              </p>
              <label className="mt-2 block text-sm font-medium text-gray-900">
                {form.tipo_entrega === 'domicilio' ? 'Fecha y hora de entrega' : 'Fecha y hora de recogida en tienda'}
              </label>
              {!empresaId ? (
                <p className="mt-2 text-sm text-amber-900">No hay empresa activa para consultar la agenda.</p>
              ) : !form.cliente?.id ? (
                <p className="mt-2 text-sm text-amber-900">Selecciona un cliente en el paso 1 para elegir un cupo.</p>
              ) : (
                <div className="mt-2">
                  <CalendarPicker
                    empresaId={empresaId}
                    selectedDate={agendaPickDate}
                    selectedTime={agendaPickTime}
                    onSelect={onAgendaCalendarSelect}
                    minDate={new Date()}
                    selectedEventTitle={form.tipo_entrega === 'domicilio' ? 'Entrega programada' : 'Recogida en tienda'}
                    selectedEventTimePrefix="a las"
                  />
                </div>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Horarios según Configuración de agenda. Los eventos y la agenda de cupos usan el mismo dato.
              </p>
            </div>
          </div>
        )}
      </div>

      <hr className="border-gray-200" />
      <div className="space-y-0 rounded-[10px] px-3 py-1" style={{ backgroundColor: C.surface }}>
        <div className="pb-2.5">
          <ToggleRow
            label="Es un regalo"
            sub="Agregar mensaje de regalo"
            on={form.es_regalo}
            onToggle={toggleRegalo}
            last
            hideSeparator
          />
          {form.es_regalo && (
            <div className="mt-3 space-y-3 rounded-lg border border-pink-200 bg-pink-50 p-3">
              <div>
                <label className="text-sm font-medium text-gray-900">Mensaje de regalo</label>
                <textarea
                  value={mensajeRegalo}
                  onChange={(e) => setMensajeRegalo(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Ej: Feliz cumpleaños, con todo mi cariño..."
                />
              </div>
              {form.tipo_entrega === 'domicilio' && (
                <div>
                  <label className="text-sm font-medium text-gray-900">Dirección de entrega del regalo</label>
                  <input
                    type="text"
                    value={direccionRegalo}
                    onChange={(e) => setDireccionRegalo(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Calle, barrio, ciudad..."
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <label className="mb-1 block text-[11px] font-semibold uppercase text-gray-500">Notas (opcional)</label>
        <textarea
          className="min-h-[80px] w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
          value={form.notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder="Instrucciones especiales…"
        />
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  sub,
  on,
  onToggle,
  last,
  hideSeparator,
}: {
  label: string;
  sub: string;
  on: boolean;
  onToggle: () => void;
  last?: boolean;
  hideSeparator?: boolean;
}) {
  return (
    <div
      className={clsx(
        'flex items-center gap-3 py-2.5',
        !last && !hideSeparator && 'border-b border-gray-200'
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900">{label}</p>
        <p className="text-[11px] text-gray-500">{sub}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className={clsx('relative h-[22px] w-[38px] shrink-0 rounded-full p-0.5 transition-colors', on ? 'bg-[#1D9E75]' : 'bg-gray-300')}
        onClick={onToggle}
      >
        <span
          className={clsx(
            'block h-[18px] w-[18px] rounded-full bg-white shadow transition-transform',
            on ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  );
}

function TotalsBox({ totales }: { totales: ReturnType<typeof useFacturaForm>['totales'] }) {
  return (
    <div className="rounded-[10px] p-3 text-sm" style={{ backgroundColor: C.surface }}>
      <div className="flex justify-between py-0.5 text-gray-600">
        <span>Subtotal</span>
        <span>{fmtCop(totales.subtotal)}</span>
      </div>
      <div className="flex justify-between py-0.5 text-gray-600">
        <span>Descuento</span>
        <span>− {fmtCop(totales.descuento)}</span>
      </div>
      <div className="flex justify-between py-0.5 text-gray-600">
        <span>IVA</span>
        <span>{fmtCop(totales.iva)}</span>
      </div>
      {totales.costo_envio > 0 ? (
        <div className="flex justify-between py-0.5 text-gray-600">
          <span>Envío</span>
          <span>{fmtCop(totales.costo_envio)}</span>
        </div>
      ) : null}
      {totales.ajuste_credito ? (
        <div className="flex justify-between py-0.5 text-gray-600">
          <span>{totales.ajuste_credito > 0 ? 'Recargo crédito' : 'Ajuste crédito'}</span>
          <span>
            {totales.ajuste_credito > 0 ? '+' : '−'} {fmtCop(Math.abs(totales.ajuste_credito))}
          </span>
        </div>
      ) : null}
      <hr className="my-1.5 border-gray-200" />
      <div className="flex justify-between text-base font-medium text-gray-900">
        <span>Total</span>
        <span>{fmtCop(totales.total)}</span>
      </div>
    </div>
  );
}

function WebStepConfirmar({
  form,
  totales,
  metodoPagoLabel,
  montoPagado,
  mensajeRegalo,
  direccionRegalo,
  fechaAgendada,
  esCredito,
  creditoModo,
  fechaVencSimple,
  numCuotas,
  frecuenciaDias,
  primeraFechaPlan,
}: {
  form: ReturnType<typeof useFacturaForm>['form'];
  totales: ReturnType<typeof useFacturaForm>['totales'];
  metodoPagoLabel: string;
  montoPagado: number;
  mensajeRegalo: string;
  direccionRegalo: string;
  fechaAgendada: string;
  esCredito: boolean;
  creditoModo: 'simple' | 'plan_pagos';
  fechaVencSimple: string;
  numCuotas: number;
  frecuenciaDias: 7 | 15 | 30;
  primeraFechaPlan: string;
}) {
  const total = totales.total;
  const saldoPendiente = Math.max(0, total - montoPagado);
  const estadoPagoUi =
    montoPagado <= 0 ? 'PENDIENTE' : saldoPendiente <= 0 ? 'PAGADO' : 'PAGO_PARCIAL';
  const nPlan = Math.min(36, Math.max(2, Math.floor(numCuotas)));
  const frecLabel =
    frecuenciaDias === 7 ? 'Semanal' : frecuenciaDias === 15 ? 'Quincenal' : 'Mensual';

  return (
    <div className="space-y-3 pt-2">
      <div className="rounded-[10px] p-3 text-sm" style={{ backgroundColor: C.surface }}>
        <SummaryRow label="Cliente" value={form.cliente?.nombre ?? '—'} />
        <SummaryRow label="Teléfono" value={form.cliente?.telefono ?? '—'} />
        <SummaryRow label="Factura" value={form.numero_factura} />
      </div>
      <div className="rounded-[10px] p-3 text-sm" style={{ backgroundColor: C.surface }}>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Productos</p>
        {form.productos.map((p) => (
          <SummaryRow key={p.id} label={`${p.nombre} ×${p.qty}`} value={fmtCop(p.precio * p.qty)} />
        ))}
      </div>
      <TotalsBox totales={totales} />
      <div className="rounded-[10px] p-3 text-sm" style={{ backgroundColor: C.surface }}>
        <SummaryRow label="Entrega" value={form.tipo_entrega === 'tienda' ? 'En tienda' : 'Domicilio'} />
        <SummaryRow
          label="Modalidad"
          value={
            form.metodo_entrega === 'recogida'
              ? 'Recogida'
              : form.metodo_entrega === 'entrega_local'
                ? 'Entrega local'
                : 'Envío / paquetería'
          }
        />
        {form.costo_envio > 0 ? <SummaryRow label="Costo envío" value={fmtCop(form.costo_envio)} /> : null}
        {form.tipo_entrega === 'domicilio' && form.direccion_entrega ? (
          <SummaryRow label="Dirección" value={form.direccion_entrega} />
        ) : null}
        {form.notas_entrega?.trim() ? <SummaryRow label="Notas entrega" value={form.notas_entrega.trim()} /> : null}
        {fechaAgendada.trim() ? (
          <SummaryRow
            label={form.tipo_entrega === 'domicilio' ? 'Domicilio agendado' : 'Recogida en tienda'}
            value={fechaAgendada.replace('T', ' ').replace(/:00(?::00)?$/, '')}
          />
        ) : (
          <SummaryRow label="Fecha (referencia)" value={form.fecha_entrega || '—'} />
        )}
        <SummaryRow label="Forma de pago" value={metodoPagoLabel} />
        <SummaryRow label="Monto pagado" value={fmtCop(montoPagado)} />
        {saldoPendiente > 0 ? <SummaryRow label="Saldo pendiente" value={fmtCop(saldoPendiente)} /> : null}
        {esCredito && saldoPendiente > 0 && creditoModo === 'simple' && (
          <SummaryRow
            label="Cobro acordado (esta venta)"
            value={`Un pago de ${fmtCop(Math.round(saldoPendiente))} — vence ${fechaVencSimple}`}
          />
        )}
        {esCredito && saldoPendiente > 0 && creditoModo === 'plan_pagos' && (
          <div className="border-t border-gray-200 py-2">
            <p className="text-[11px] font-semibold uppercase text-gray-500">Plan de cuotas (esta venta)</p>
            <p className="text-xs text-gray-600">
              {nPlan} pagos · {frecLabel} · primera: {primeraFechaPlan}
            </p>
            <ul className="mt-1 max-h-36 space-y-0.5 overflow-y-auto text-[11px] text-gray-800">
              {partesIgualesCOP(total, nPlan).map((m, i) => {
                const fechas = fechasPlanCuotas(primeraFechaPlan, nPlan, frecuenciaDias);
                return (
                  <li key={i} className="flex justify-between gap-2">
                    <span>
                      Cuota {i + 1} · {fechas[i] || '—'}
                    </span>
                    <span className="font-medium">{fmtCop(Math.round(m))}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-1 text-[10px] text-gray-500">
              Incluye el total de la factura; el abono de hoy se aplica al guardar.
            </p>
          </div>
        )}
        <div className="flex justify-between py-1">
          <span className="text-gray-600">Estado pago</span>
          <span
            className={clsx(
              'rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
              esCredito && saldoPendiente > 0
                ? 'bg-blue-100 text-blue-800'
                : estadoPagoUi === 'PAGADO'
                ? 'bg-green-100 text-green-800'
                : estadoPagoUi === 'PAGO_PARCIAL'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-red-100 text-red-800'
            )}
          >
            {esCredito && saldoPendiente > 0
              ? 'Crédito'
              : estadoPagoUi === 'PAGADO'
                ? 'Pagada'
                : estadoPagoUi === 'PAGO_PARCIAL'
                  ? 'Pago parcial'
                  : 'Pendiente'}
          </span>
        </div>
        {form.es_regalo && (
          <>
            <SummaryRow label="Regalo" value="✓ Marcado como regalo" />
            {mensajeRegalo.trim() ? <SummaryRow label="Mensaje" value={mensajeRegalo} /> : null}
            {form.tipo_entrega === 'domicilio' && direccionRegalo.trim() ? (
              <SummaryRow label="Dir. regalo" value={direccionRegalo} />
            ) : null}
          </>
        )}
        {form.notas ? <SummaryRow label="Notas" value={form.notas} /> : null}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 py-1">
      <span className="text-gray-600">{label}</span>
      <span className="max-w-[60%] text-right font-medium text-gray-900">{value}</span>
    </div>
  );
}
